import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, linksAPI, dronesAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Radio, Link2, Plane, Users, Plus, Trash2, ExternalLink, Maximize2,
  Shield, FileText, Eye, EyeOff, Copy, AlertTriangle, ChevronDown, Share2,
  Phone, Mail, LogOut, UserPlus, Send, Bell, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import FullscreenPlayer from '../components/FullscreenPlayer';
import axios from 'axios';

const API_URL = '/api';

const MyProjectPage = () => {
  const { user, isAdmin, isSupervisor, isPilot, isSuperAdmin, isLeader, isVigilant, userProjectIds, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canManage = isAdmin || isSupervisor || isSuperAdmin;
  const isLimitedRole = isPilot || isLeader || isVigilant;

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [project, setProject] = useState(null);
  const [links, setLinks] = useState([]);
  const [drones, setDrones] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('streams');
  const [error, setError] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);

  // Credential generation
  const [showCredModal, setShowCredModal] = useState(false);
  const [credName, setCredName] = useState('');
  const [credRole, setCredRole] = useState('pilot');
  const [generatedCreds, setGeneratedCreds] = useState(null);

  // Streaming link
  const [showStreamModal, setShowStreamModal] = useState(false);
  const [streamName, setStreamName] = useState('');
  const [streamUrl, setStreamUrl] = useState('');

  const hasMultipleProjects = (isAdmin || isSuperAdmin) && userProjectIds.length > 1;

  useEffect(() => {
    const fetchProjects = async () => {
      if ((isAdmin || isSuperAdmin) && userProjectIds.length > 1) {
        try {
          const res = await projectsAPI.list();
          setProjects(res.data);
          if (res.data.length > 0 && !selectedProjectId) setSelectedProjectId(res.data[0].id);
        } catch (err) { console.error(err); }
      } else {
        const pid = user?.project_id || (userProjectIds.length > 0 ? userProjectIds[0] : null);
        if (pid) setSelectedProjectId(pid);
      }
    };
    if (user) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchProjectData = useCallback(async (projectId) => {
    if (!projectId) { setError('Usuario nao vinculado a um projeto'); setLoading(false); return; }
    try {
      const [projectRes, linksRes, dronesRes, usersRes] = await Promise.all([
        projectsAPI.get(projectId),
        linksAPI.list(projectId),
        dronesAPI.list(projectId),
        canManage ? usersAPI.list() : Promise.resolve({ data: [] })
      ]);
      setProject(projectRes.data);
      setLinks(linksRes.data.filter(l => l.project_id === projectId));
      setDrones(dronesRes.data.filter(d => d.project_id === projectId));
      setUsers(usersRes.data.filter(u => u.project_id === projectId));
      setError(null);
    } catch (err) { setError('Erro ao carregar dados do projeto'); }
    finally { setLoading(false); }
  }, [canManage]);

  useEffect(() => {
    if (selectedProjectId) { setLoading(true); fetchProjectData(selectedProjectId); }
    else if (user && !selectedProjectId) { setLoading(false); setError('Usuario nao vinculado a um projeto'); }
  }, [selectedProjectId, fetchProjectData, user]);

  // Generate credentials
  const handleGenerateCredentials = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/projects/${selectedProjectId}/generate-credentials`, {
        name: credName, role: credRole
      }, { headers });
      setGeneratedCreds(res.data);
      toast.success(`Credenciais geradas para ${credName}!`);
      fetchProjectData(selectedProjectId);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao gerar credenciais'); }
  };

  const copyCredentials = () => {
    if (!generatedCreds) return;
    const appUrl = window.location.origin;
    const roleLabels = { supervisor: 'Supervisor', pilot: 'Piloto', leader: 'Lider', vigilant: 'Vigilante' };
    const text = `Acesso RPAS Monitor\nLink: ${appUrl}\nUsuario: ${generatedCreds.username}\nSenha: ${generatedCreds.password}\nProjeto: ${generatedCreds.project_name}\nPerfil: ${roleLabels[generatedCreds.role] || generatedCreds.role}`;
    navigator.clipboard.writeText(text);
    toast.success('Credenciais copiadas!');
  };

  const shareCredentialsWhatsApp = () => {
    if (!generatedCreds) return;
    const appUrl = window.location.origin;
    const roleLabels = { supervisor: 'Supervisor', pilot: 'Piloto', leader: 'Lider', vigilant: 'Vigilante' };
    const text = encodeURIComponent(`*Acesso RPAS Monitor*\nLink: ${appUrl}\nUsuario: ${generatedCreds.username}\nSenha: ${generatedCreds.password}\nProjeto: ${generatedCreds.project_name}\nPerfil: ${roleLabels[generatedCreds.role] || generatedCreds.role}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareCredentialsEmail = () => {
    if (!generatedCreds) return;
    const appUrl = window.location.origin;
    const roleLabels = { supervisor: 'Supervisor', pilot: 'Piloto', leader: 'Lider', vigilant: 'Vigilante' };
    const subject = encodeURIComponent(`Acesso RPAS Monitor - ${generatedCreds.project_name}`);
    const body = encodeURIComponent(`Acesso ao sistema RPAS Monitor:\n\nLink: ${appUrl}\nUsuario: ${generatedCreds.username}\nSenha: ${generatedCreds.password}\nProjeto: ${generatedCreds.project_name}\nPerfil: ${roleLabels[generatedCreds.role] || generatedCreds.role}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Add streaming link
  const handleAddStream = async (e) => {
    e.preventDefault();
    if (!project) return;
    try {
      const updatedPlayers = [...(project.players || []), { id: `player_${Date.now()}`, name: streamName, url: streamUrl }];
      await axios.put(`${API_URL}/projects/${selectedProjectId}`, { players: updatedPlayers }, { headers });
      toast.success('Stream adicionado!');
      setShowStreamModal(false); setStreamName(''); setStreamUrl('');
      fetchProjectData(selectedProjectId);
    } catch (error) { toast.error('Erro ao adicionar stream'); }
  };

  // Pilot clock-out
  const handleClockOut = async () => {
    try {
      await axios.post(`${API_URL}/pilot/clock-out`, {}, { headers });
      toast.success('Ponto de saida registrado!');
    } catch (e) { toast.error('Erro ao registrar saida'); }
  };

  // Pilot flight alert
  const handleFlightAlert = async () => {
    try {
      await axios.post(`${API_URL}/pilot/flight-alert`, {}, { headers });
      toast.success('Alerta INICIANDO VOO enviado!');
    } catch (e) { toast.error('Erro ao enviar alerta'); }
  };

  const handleShareWhatsApp = (link) => {
    const url = encodeURIComponent(`${window.location.origin}/stream/${link.access_token}`);
    const text = encodeURIComponent(`Acesse o stream: ${link.name}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const handleShareEmail = (link) => {
    const shareUrl = `${window.location.origin}/stream/${link.access_token}`;
    const subject = encodeURIComponent(`Stream RPAS: ${link.name}`);
    const body = encodeURIComponent(`Acesse o stream:\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(`${window.location.origin}/stream/${link.access_token}`);
    toast.success('Link copiado!');
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza?')) return;
    try { await usersAPI.delete(userId); toast.success('Usuario removido!'); fetchProjectData(selectedProjectId); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir'); }
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diff = new Date(date) - new Date();
    return diff < 30 * 24 * 60 * 60 * 1000 && diff > 0;
  };
  const isExpired = (date) => date && new Date(date) < new Date();

  const getLogoUrl = (logoPath) => {
    if (!logoPath || !token) return null;
    return `/api/files/${logoPath}?auth=${token}`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" /></div>;

  if (error || !project) return (
    <div className="card-hud p-8 text-center">
      <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
      <p className="text-gray-500">{error || 'Voce nao esta vinculado a nenhum projeto'}</p>
    </div>
  );

  const players = project.players || [];
  const externalLinks = project.external_links || [];
  const projectMembers = users.filter(u => u.role !== 'super_admin');
  const tabCount = isLimitedRole ? 2 : canManage ? 5 : 3;

  return (
    <div className="space-y-6" data-testid="my-project-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          {project.logo_url && (
            <img src={getLogoUrl(project.logo_url)} alt="" className="w-14 h-14 object-contain rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="project-header-logo" />
          )}
          <div>
            <h1 className="text-2xl font-rajdhani font-bold text-navy uppercase tracking-wider">{project.name}</h1>
            {project.description && <p className="text-gray-500 text-sm mt-1">{project.description}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {isPilot && (
            <>
              <Button onClick={handleFlightAlert} className="bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs" data-testid="pilot-flight-alert">
                <Bell size={14} className="mr-1" /> INICIANDO VOO
              </Button>
              <Button onClick={handleClockOut} className="bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs" data-testid="pilot-clock-out">
                <LogOut size={14} className="mr-1" /> REGISTRAR SAIDA
              </Button>
            </>
          )}
          {hasMultipleProjects && (
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="w-[260px] input-hud" data-testid="project-switcher">
                <ChevronDown size={14} className="mr-2 text-gold" />
                <SelectValue placeholder="Selecione o projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-hud p-4 flex items-center gap-3 animate-slide-up">
          <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center"><Radio size={20} className="text-gold" /></div>
          <div><p className="text-gray-500 text-xs font-mono">STREAMS</p><p className="text-xl font-bold text-navy">{players.length}</p></div>
        </div>
        {canManage && (
          <div className="card-hud p-4 flex items-center gap-3 animate-slide-up" style={{animationDelay:'0.1s'}}>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Link2 size={20} className="text-blue-600" /></div>
            <div><p className="text-gray-500 text-xs font-mono">LINKS</p><p className="text-xl font-bold text-navy">{links.length + externalLinks.length}</p></div>
          </div>
        )}
        <div className="card-hud p-4 flex items-center gap-3 animate-slide-up" style={{animationDelay:'0.2s'}}>
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><Plane size={20} className="text-green-600" /></div>
          <div><p className="text-gray-500 text-xs font-mono">DRONES</p><p className="text-xl font-bold text-navy">{drones.length}</p></div>
        </div>
        {canManage && (
          <div className="card-hud p-4 flex items-center gap-3 animate-slide-up" style={{animationDelay:'0.3s'}}>
            <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center"><Users size={20} className="text-cyan-600" /></div>
            <div><p className="text-gray-500 text-xs font-mono">MEMBROS</p><p className="text-xl font-bold text-navy">{projectMembers.length}</p></div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 border border-gray-200 p-1 w-full grid" style={{gridTemplateColumns: `repeat(${tabCount}, 1fr)`}}>
          <TabsTrigger value="streams" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm text-xs font-medium"><Radio size={14} className="mr-1" /> STREAMS</TabsTrigger>
          {canManage && <TabsTrigger value="links" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm text-xs font-medium"><Link2 size={14} className="mr-1" /> LINKS</TabsTrigger>}
          <TabsTrigger value="drones" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm text-xs font-medium"><Plane size={14} className="mr-1" /> DRONES</TabsTrigger>
          {canManage && <TabsTrigger value="members" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm text-xs font-medium"><Users size={14} className="mr-1" /> EQUIPE</TabsTrigger>}
          {canManage && <TabsTrigger value="docs" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm text-xs font-medium"><FileText size={14} className="mr-1" /> DOCS</TabsTrigger>}
        </TabsList>

        {/* Streams Tab */}
        <TabsContent value="streams" className="mt-6">
          {canManage && (
            <div className="flex justify-end mb-4">
              <Dialog open={showStreamModal} onOpenChange={setShowStreamModal}>
                <DialogTrigger asChild>
                  <Button className="btn-primary text-xs" data-testid="add-stream-btn"><Plus size={16} className="mr-1" /> ADICIONAR STREAM</Button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-gray-200 max-w-md">
                  <DialogHeader><DialogTitle className="text-navy font-rajdhani uppercase tracking-wider">ADICIONAR STREAM</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddStream} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase">Nome do Stream</Label>
                      <Input value={streamName} onChange={(e) => setStreamName(e.target.value)} className="input-hud" placeholder="Ex: Camera 01" required />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase">URL do Stream</Label>
                      <Input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} className="input-hud" placeholder="https://..." required />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowStreamModal(false)} className="btn-secondary flex-1">CANCELAR</Button>
                      <Button type="submit" className="btn-primary flex-1">ADICIONAR</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {players.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {players.map((player, index) => (
                <div key={player.id || index} className="card-hud overflow-hidden animate-scale-in" style={{animationDelay:`${index*0.1}s`}}>
                  <div className="aspect-video">
                    <iframe src={player.url} className="w-full h-full" frameBorder="0" allowFullScreen title={player.name} />
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between bg-white border-t border-gray-100">
                    <p className="text-navy text-sm font-medium truncate flex-1 min-w-0">{player.name}</p>
                    <div className="flex gap-1 flex-shrink-0">
                      {canManage && (
                        <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-7 w-7"
                          onClick={async () => {
                            if (!window.confirm('Excluir este stream?')) return;
                            try {
                              const updated = players.filter((_, i) => i !== index);
                              await axios.put(`${API_URL}/projects/${selectedProjectId}`, { players: updated }, { headers });
                              toast.success('Stream removido!');
                              fetchProjectData(selectedProjectId);
                            } catch (e) { toast.error('Erro ao remover stream'); }
                          }} data-testid={`delete-player-${index}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="text-gray-300 hover:text-gold hover:bg-gold/10 h-7 w-7" onClick={() => setExpandedVideo(player)}>
                        <Maximize2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center"><Radio size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum stream configurado</p></div>
          )}
        </TabsContent>

        {/* Links Tab */}
        <TabsContent value="links" className="mt-6">
          <div className="space-y-6">
            {/* Create custom link form - for admin/supervisor */}
            {canManage && (
              <div className="card-hud p-4 border-gold/30 bg-gold/5">
                <h3 className="text-gold font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Plus size={16} /> CRIAR LINK PERSONALIZADO
                </h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const name = formData.get('link_name');
                  const iframe_url = formData.get('link_url');
                  const expiry = formData.get('link_expiry') || '30d';
                  if (!iframe_url) { toast.error('Informe a URL do link'); return; }
                  try {
                    await linksAPI.create({
                      name: name || 'Link Personalizado',
                      iframe_url,
                      project_id: selectedProjectId,
                      expiry_option: expiry
                    });
                    toast.success('Link criado com sucesso!');
                    e.target.reset();
                    fetchProjectData(selectedProjectId);
                  } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar link'); }
                }} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Nome do Link</Label>
                    <Input name="link_name" className="input-hud" placeholder="Ex: Camera Norte" data-testid="custom-link-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-500 font-mono text-xs uppercase">URL *</Label>
                    <Input name="link_url" className="input-hud" placeholder="https://..." required data-testid="custom-link-url" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Validade</Label>
                    <select name="link_expiry" defaultValue="30d" className="w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600 focus:border-gold focus:ring-1 focus:ring-gold" data-testid="custom-link-expiry">
                      <option value="24h">24 horas</option>
                      <option value="3d">3 dias</option>
                      <option value="7d">7 dias</option>
                      <option value="30d">30 dias</option>
                      <option value="90d">90 dias</option>
                      <option value="6m">6 meses</option>
                      <option value="1y">1 ano</option>
                      <option value="unlimited">Sem limite</option>
                    </select>
                  </div>
                  <Button type="submit" className="btn-primary h-10" data-testid="create-custom-link-btn">
                    <Plus size={16} className="mr-2" /> CRIAR LINK
                  </Button>
                </form>
              </div>
            )}

            {links.length > 0 && (
              <div>
                <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4">Links Gerados</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {links.map((link) => (
                    <div key={link.id} className="card-hud p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-navy font-medium truncate flex-1">{link.name}</h4>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleCopyLink(link)} className="text-blue-600 hover:text-gold hover:bg-gold/10 h-8 w-8"><Copy size={14} /></Button>
                          {canManage && (
                            <Button variant="ghost" size="icon" onClick={async () => {
                              if (!window.confirm('Excluir este link?')) return;
                              try { await linksAPI.delete(link.id); toast.success('Link excluido!'); fetchProjectData(selectedProjectId); }
                              catch (e) { toast.error('Erro ao excluir'); }
                            }} className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8" data-testid={`delete-link-${link.id}`}><Trash2 size={14} /></Button>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <Button onClick={() => handleShareWhatsApp(link)} className="flex-1 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 text-xs rounded-lg"><Phone size={14} className="mr-1" /> WhatsApp</Button>
                        <Button onClick={() => handleShareEmail(link)} className="flex-1 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs rounded-lg"><Mail size={14} className="mr-1" /> E-mail</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {externalLinks.length > 0 && (
              <div>
                <h3 className="text-blue-600 font-rajdhani font-semibold uppercase tracking-wider mb-4">Links Externos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {externalLinks.map((link) => (
                    <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="card-hud p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-3"><ExternalLink size={20} className="text-blue-600" /><div className="min-w-0"><h4 className="text-navy font-medium truncate">{link.name}</h4><p className="text-gray-400 text-xs font-mono truncate">{link.url}</p></div></div>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {links.length === 0 && externalLinks.length === 0 && (
              <div className="card-hud p-8 text-center"><Link2 size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum link disponivel</p></div>
            )}
          </div>
        </TabsContent>

        {/* Drones Tab */}
        <TabsContent value="drones" className="mt-6">
          {drones.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drones.map((drone, idx) => (
                <div key={drone.id} className="card-hud p-4 space-y-3 animate-slide-up" style={{animationDelay:`${idx*0.05}s`}}>
                  <div className="flex items-center gap-3">
                    <Plane size={24} className="text-gold" />
                    <div>
                      <h4 className="text-navy font-rajdhani font-semibold uppercase">{drone.serial_number}</h4>
                      <p className="text-blue-600 text-sm font-mono">{drone.model}</p>
                    </div>
                  </div>
                  {drone.insurance_expiry && (
                    <div className="flex items-center gap-2">
                      <Shield size={14} className={isExpired(drone.insurance_expiry) ? 'text-red-500' : isExpiringSoon(drone.insurance_expiry) ? 'text-amber-500' : 'text-green-500'} />
                      <span className="text-gray-500 text-xs font-mono">Seguro: {new Date(drone.insurance_expiry).toLocaleDateString('pt-BR')}</span>
                      {isExpired(drone.insurance_expiry) && <span className="badge-danger">EXPIRADO</span>}
                      {isExpiringSoon(drone.insurance_expiry) && !isExpired(drone.insurance_expiry) && <span className="badge-warning">VENCENDO</span>}
                    </div>
                  )}
                  {drone.documents?.length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-gray-100">
                      <p className="text-gray-400 text-xs font-mono uppercase">Documentos:</p>
                      {drone.documents.map((doc, i) => (
                        <a key={i} href={`${doc.file_url || doc.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 text-xs hover:text-gold">
                          <FileText size={12} />{doc.filename || doc.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center"><Plane size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum drone cadastrado</p></div>
          )}
        </TabsContent>

        {/* Members Tab (Admin/Supervisor) */}
        {canManage && (
          <TabsContent value="members" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={showCredModal} onOpenChange={(v) => { setShowCredModal(v); if (!v) setGeneratedCreds(null); }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="generate-creds-btn"><UserPlus size={18} className="mr-2" />GERAR CREDENCIAIS</Button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-gray-200 max-w-md">
                  <DialogHeader><DialogTitle className="text-navy font-rajdhani uppercase tracking-wider">GERAR CREDENCIAIS</DialogTitle></DialogHeader>
                  
                  {!generatedCreds ? (
                    <form onSubmit={handleGenerateCredentials} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-gray-600 font-mono text-xs uppercase">Nome Completo</Label>
                        <Input value={credName} onChange={(e) => setCredName(e.target.value)} className="input-hud" placeholder="Ex: Joao Silva" required />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-600 font-mono text-xs uppercase">Perfil de Acesso</Label>
                        <Select value={credRole} onValueChange={setCredRole}>
                          <SelectTrigger className="input-hud"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="supervisor">Supervisor (acesso total ao projeto)</SelectItem>
                            <SelectItem value="pilot">Piloto (acesso padrao)</SelectItem>
                            <SelectItem value="leader">Lider (Painel, Mapa, Chat)</SelectItem>
                            <SelectItem value="vigilant">Vigilante (Painel, Mapa, Chat)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowCredModal(false)} className="btn-secondary flex-1">CANCELAR</Button>
                        <Button type="submit" className="btn-primary flex-1">GERAR</Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4 mt-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                        <p className="text-green-700 font-medium text-sm">Credenciais geradas com sucesso!</p>
                        <div className="bg-white rounded p-3 border border-green-100 space-y-1 font-mono text-sm">
                          <p><span className="text-gray-500">Nome:</span> <span className="text-navy font-bold">{generatedCreds.name}</span></p>
                          <p><span className="text-gray-500">Usuario:</span> <span className="text-navy font-bold">{generatedCreds.username}</span></p>
                          <p><span className="text-gray-500">Senha:</span> <span className="text-navy font-bold">{generatedCreds.password}</span></p>
                          <p><span className="text-gray-500">Perfil:</span> <span className="text-navy font-bold">{{ supervisor: 'Supervisor', pilot: 'Piloto', leader: 'Lider', vigilant: 'Vigilante' }[generatedCreds.role] || generatedCreds.role}</span></p>
                          <p><span className="text-gray-500">Projeto:</span> <span className="text-navy font-bold">{generatedCreds.project_name}</span></p>
                        </div>
                      </div>
                      <Button onClick={copyCredentials} className="w-full btn-secondary text-xs" data-testid="copy-creds-btn"><Copy size={14} className="mr-2" />COPIAR CREDENCIAIS</Button>
                      <div className="grid grid-cols-2 gap-3">
                        <Button onClick={shareCredentialsWhatsApp} className="bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 rounded-lg h-11 text-xs" data-testid="share-creds-whatsapp"><Phone size={14} className="mr-1" /> WhatsApp</Button>
                        <Button onClick={shareCredentialsEmail} className="bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-lg h-11 text-xs" data-testid="share-creds-email"><Mail size={14} className="mr-1" /> E-mail</Button>
                      </div>
                      <Button onClick={() => { setGeneratedCreds(null); setCredName(''); }} className="w-full btn-primary text-xs">GERAR OUTRA</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
            {projectMembers.length > 0 ? (
              <div className="card-hud overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase">Nome</th>
                      <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase">Usuario</th>
                      <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase">Perfil</th>
                      <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase">Criado em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectMembers.map((m) => (
                      <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="p-4 text-navy">{m.name || '-'}</td>
                        <td className="p-4 text-gray-600 font-mono text-sm">{m.username}</td>
                        <td className="p-4"><span className={`text-xs font-mono uppercase px-2 py-1 rounded ${m.role === 'supervisor' ? 'bg-blue-50 text-blue-700' : m.role === 'admin' ? 'bg-amber-50 text-amber-700' : m.role === 'leader' ? 'bg-purple-50 text-purple-700' : m.role === 'vigilant' ? 'bg-cyan-50 text-cyan-700' : 'bg-green-50 text-green-700'}`}>{{ supervisor: 'Supervisor', admin: 'Admin', pilot: 'Piloto', leader: 'Lider', vigilant: 'Vigilante' }[m.role] || m.role}</span></td>
                        <td className="p-4 text-gray-500 text-sm font-mono">{new Date(m.created_at).toLocaleDateString('pt-BR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-hud p-8 text-center"><Users size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum membro cadastrado</p></div>
            )}
          </TabsContent>
        )}

        {/* Documents Tab */}
        {canManage && (
          <TabsContent value="docs" className="mt-6">
            <div className="flex justify-end mb-4">
              <label className="btn-primary cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium">
                <Plus size={16} /> ANEXAR DOCUMENTO
                <input type="file" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    await axios.post(`${API_URL}/projects/${selectedProjectId}/upload-document`, formData, { headers });
                    toast.success('Documento anexado!');
                    fetchProjectData(selectedProjectId);
                  } catch (e) { toast.error('Erro ao anexar documento'); }
                  e.target.value = '';
                }} data-testid="upload-project-doc" />
              </label>
            </div>
            {(project.documents || []).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {project.documents.map((doc, idx) => (
                  <div key={doc.id || idx} className="card-hud p-4 hover:border-gold/50 transition-colors flex items-center gap-3">
                    <a href={`${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText size={24} className="text-gold flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-navy font-medium text-sm truncate">{doc.filename}</p>
                        <p className="text-gray-400 text-xs font-mono">{doc.uploaded_by} - {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</p>
                      </div>
                    </a>
                    <Button variant="ghost" size="icon" className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 flex-shrink-0" data-testid={`delete-doc-${doc.id}`} onClick={async (e) => {
                      e.preventDefault();
                      if (!window.confirm('Excluir este documento?')) return;
                      try {
                        await axios.delete(`${API_URL}/projects/${selectedProjectId}/documents/${doc.id}`, { headers });
                        toast.success('Documento excluido!');
                        fetchProjectData(selectedProjectId);
                      } catch (err) { toast.error('Erro ao excluir documento'); }
                    }}><Trash2 size={14} /></Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card-hud p-8 text-center"><FileText size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum documento anexado</p></div>
            )}
          </TabsContent>
        )}
      </Tabs>

      <FullscreenPlayer open={!!expandedVideo} onClose={() => setExpandedVideo(null)} videoUrl={expandedVideo?.url} videoName={expandedVideo?.name} />
    </div>
  );
};

export default MyProjectPage;
